import { PUBLIC_UPLOADCARE_PUBLICKEY } from "$env/static/public";
import { UploadClient } from "@uploadcare/upload-client";

export interface UploadedFile {
  uuid: string;
  src: string;
  type: string;
}

export const uploadcareClient = new UploadClient({
  publicKey: PUBLIC_UPLOADCARE_PUBLICKEY,
});

/**
 * Uploads a file from an input element to Uploadcare and returns the CDN URL
 * Checks if the input element contains a file first
 * @param input The file input element
 * @param store Whether to store the file in Uploadcare, defaults to `true`
 *
 * @returns The CDN URL of the uploaded file
 */
export const uploadToUploadcare = async (
  input: HTMLInputElement,
  store = true
): Promise<{ uuid: string; src: string }> => {
  if (input.files?.[0]) {
    return await uploadFileToUploadcare(input.files[0], store);
  } else {
    throw new Error("No file selected");
  }
};

/**
 * Uploads a File to Uploadcare and returns the CDN URL
 * @param file The file/url to upload
 * @param store Whether to store the file in Uploadcare, defaults to `true`
 *
 * @returns The CDN URL of the uploaded file
 */
export const uploadFileToUploadcare = async (file: File | string, store = true, metadata?: any) => {
  try {
    const ucareFile = await uploadcareClient.uploadFile(file, {
      store,
      metadata,
    });
    return {
      uuid: ucareFile.uuid,
      src: ucareFile.cdnUrl!,
      type: ucareFile.mimeType || "",
    } as UploadedFile;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown error";
    throw new Error(message);
  }
};

export const uploadToUploadcareFromFilePickerOrUrl = async (
  files?: FileList,
  originalImage?: string | null,
  imageUrl?: string | null
) => {
  let file: File | string | null | undefined = files?.[0];
  if (!file) {
    if (originalImage === imageUrl) {
      return;
    }
    file = imageUrl;
  }

  if (!file) {
    return;
  }

  const response = await uploadcareClient.uploadFile(file);

  return response.cdnUrl!;
};
